import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../client';

export interface Booking {
  id: number;
  consumerId: number;
  therapistId: number;
  slotDate: string;
  slotTime: string;
  sessionType: 'INTAKE' | 'REGULAR' | 'CRISIS';
  status: 'PENDING_PAYMENT' | 'CONFIRMED' | 'CANCELLED' | 'COMPLETED';
  amountCents: number;
  platformFeeCents: number;
  therapistAmountCents: number;
  notes?: string;
  cancelledAt?: string;
  cancellationReason?: string;
  createdAt: string;
  updatedAt: string;
  consumerName?: string;
  consumerEmail?: string;
}

export interface BookingsResponse {
  bookings: Booking[];
  total: number;
}

export function useTherapistBookings() {
  return useQuery({
    queryKey: ['therapist-bookings'],
    queryFn: async () => {
      const response = await apiClient.get<BookingsResponse>(
        '/bookings/therapist/my-bookings'
      );
      return response.data;
    },
  });
}

export function useBookingDetail(bookingId: number | undefined) {
  return useQuery({
    queryKey: ['booking', bookingId],
    queryFn: async () => {
      const response = await apiClient.get<Booking>(`/bookings/${bookingId}`);
      return response.data;
    },
    enabled: !!bookingId,
  });
}

export function useCancelBooking() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ bookingId, reason }: { bookingId: number; reason?: string }) => {
      const response = await apiClient.post(`/bookings/${bookingId}/cancel`, {
        reason: reason || 'Cancelled by therapist',
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['therapist-bookings'] });
      queryClient.invalidateQueries({ queryKey: ['booking'] });
    },
  });
}

export function useChangePassword() {
  return useMutation({
    mutationFn: async (data: { currentPassword: string; newPassword: string }) => {
      const response = await apiClient.post('/auth/change-password', data);
      return response.data;
    },
  });
}

export function useUpdateProfile() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: Record<string, unknown>) => {
      const response = await apiClient.put('/therapist/profile', data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['auth'] });
    },
  });
}

export function useTherapistProfile() {
  return useQuery({
    queryKey: ['therapist-profile'],
    queryFn: async () => {
      const response = await apiClient.get('/therapist/profile');
      return response.data;
    },
  });
}
